import type { ReactNode } from 'react';
import { useActions, useLive, useRuntime } from './context';
import { isoDayIn } from '../mirror/time';
import type { ClientDetail } from '../wire';
import { useLocale, useT } from '../messages';
import type { T, UiLocale } from '../messages';

/**
 * **"Text her appointment reminders"** — the desk's half of consent
 * (spec §2.6.1(a), migration 0041).
 *
 * Reminders have been able to send since migration 0037 and have reached
 * nobody, because `clients.sms_opt_in` is authoritative when we own messaging
 * and **nothing in the product collected it**. This is one of the two paths
 * that does; the guest site's unticked box is the other.
 *
 * ## Why this is a component and not a checkbox written twice
 *
 * It is drawn in two places — the client record and the appointment sheet —
 * because those are the two moments a staff member is actually talking to the
 * person: reviewing her record, or standing in front of her. A second copy
 * would drift, and this codebase has already made that argument twice and
 * meant it: one scheduling engine (`packages/scheduling`, "a second
 * implementation anywhere is a bug by definition") and one locale fallback
 * (`localize`, "for the same reason there is one scheduling engine"). A second
 * consent control is worse than either, because the thing that would drift is
 * *what we told her we would do*.
 *
 * ## The label is a promise and the script is on the screen
 *
 * "Text her appointment reminders" rather than "SMS reminders: on" — a setting
 * describes a system, a promise describes what somebody was told. And the
 * sentence a staff member should say is printed **beside the control, not in a
 * tooltip**: the tick is only honest if somebody asked, and the cheapest way to
 * make sure somebody asked is to put the words where the thumb already is. A
 * tooltip is a place to hide a legal obligation.
 *
 * ## Since-when, in plain words, and never invented
 *
 * A row with the flag on and **no `sms_opt_in_at`** predates 0041 — an import,
 * a seed, or the years the column was a bare boolean. It says exactly that. The
 * alternative was to show the row's `updated_at`, which is a date about
 * *something* and would read as a date about *this*, and inventing a consent
 * date is the failure mode the whole migration exists to end.
 *
 * ## Offline, and a display-side overlay rather than a merge
 *
 * The write is `actions.updateClient`, which is the outbox path every other
 * edit on this screen takes, so ticking the box with no bars queues and
 * replays. A queued change is overlaid on the mirrored row and **marked** —
 * `ClientLengths`' established pattern, and the same caveat: nothing is
 * written, nothing is combined, and the moment the intent settles the real row
 * is what draws. The provenance line follows the mirror rather than the
 * overlay for the same reason: *when* and *how* are the server's answer, and a
 * device that guessed at them would be inventing the fact this panel exists to
 * report.
 */
export function SmsReminders({
  client,
  heading = true,
}: {
  client: ClientDetail;
  /**
   * Draw the `Reminders` heading. `false` on the client record, where
   * `Permissions` owns the group's one heading and this is a row inside it;
   * `true` on the appointment sheet, where the control stands alone.
   */
  heading?: boolean;
}): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const t = useT();
  const locale = useLocale();
  const session = useLive(() => runtime.mirror.session(), null);
  const timezone = session?.tenant.timezone ?? 'UTC';
  const pending = useLive(() => runtime.outbox.pending(), []);

  /**
   * **A client with no phone cannot be reminded**, so she is told that instead
   * of being offered a tick that does nothing. `clients.noIdentity` is the
   * precedent: the app says what is missing and what adding it would buy,
   * rather than drawing a control whose only possible outcome is silence.
   *
   * An email identity is not a phone. Reminders are SMS (§2.6.1) and there is
   * no email reminder to fall back to, so an inbox is not a destination here.
   */
  const hasPhone = client.identities.some((i) => i.identity_kind === 'phone');

  /**
   * The most recent queued `client.update` for this client that carries the
   * flag. Most recent rather than first: two taps queue two intents, they drain
   * in order, and the one that will land last is the one the owner meant.
   */
  const queued = pending
    .filter(
      (entry) =>
        entry.kind === 'client.update' &&
        entry.target['client_id'] === client.client_id &&
        typeof entry.payload['sms_opt_in'] === 'boolean',
    )
    .at(-1);

  const optIn = queued === undefined ? client.sms_opt_in : queued.payload['sms_opt_in'] === true;

  return (
    <div className="permission">
      {heading ? <h3>{t('sms.heading')}</h3> : null}

      {hasPhone ? (
        <div className="reminders">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={optIn}
              onChange={(e) => {
                void actions.updateClient({
                  clientId: client.client_id,
                  name: client.display_name,
                  // One field, deliberately. The changes bag is arbitrary and
                  // the whole panel could have posted a name and a note
                  // alongside; a consent write that carried unrelated edits
                  // would make "who changed her consent, and when" a question
                  // about a diff rather than about an intent.
                  changes: { sms_opt_in: e.target.checked },
                });
              }}
            />
            <span>{t('sms.tick')}</span>
          </label>

          {/* Not a tooltip, not a title attribute, not behind a disclosure.
              See the note above: this sentence is what makes the tick true. */}
          <p className="reminders__script">
            {t('sms.ask')} <q>{t('sms.script')}</q>
          </p>

          {queued === undefined ? null : (
            <span className="badge badge--pending">{t('today.waitingToSend')}</span>
          )}

          <p className="muted">
            {client.sms_opt_in
              ? provenanceLine(client, timezone, runtime.now(), locale, t)
              : t('sms.off')}
          </p>
        </div>
      ) : (
        <p className="muted">{t('sms.noPhone')}</p>
      )}
    </div>
  );
}

/**
 * "Since 3 March — she asked on the website", or the honest admission that we
 * do not know.
 *
 * Reads the **mirrored** row, never the overlay: a tick that has not left the
 * device has no provenance yet, and the panel marks it as waiting instead of
 * dating it. Exported for the suite, which asserts each of the five sources in
 * both languages — the one part of this file that is genuinely easy to get
 * wrong by pasting.
 */
export function provenanceLine(
  client: ClientDetail,
  timezone: string,
  nowMs: number,
  locale: UiLocale,
  t: T,
): string {
  // `?? null` rather than trusting the type: a row this device wrote before
  // migration 0041 is read back out of IndexedDB without these keys, whatever
  // `wire.ts` says (`ClientList`'s note).
  const at = client.sms_opt_in_at ?? null;
  const source = client.sms_opt_in_source ?? null;

  // On, with no record of when. Not a guess, not `updated_at`, not "recently".
  if (at === null) return t('sms.sinceUnknown');

  return t('sms.since', {
    day: formatConsentDay(at, timezone, nowMs, locale),
    how: t(HOW_KEYS[source ?? 'import_none']),
  });
}

/**
 * The six sources of `SMS_OPT_IN_SOURCES`, as a person reads them.
 *
 * A table rather than a chain of ternaries, so adding a source to the domain
 * and forgetting the words here is a compile error rather than a blank clause.
 * A `null` source falls to `import_none`, which is what a `null` *means* on
 * this column: the flag arrived from somewhere that recorded nothing.
 */
const HOW_KEYS = {
  staff: 'sms.howStaff',
  guest_site: 'sms.howGuestSite',
  reply_yes: 'sms.howReplyYes',
  reply_stop: 'sms.howReplyStop',
  import_none: 'sms.howImport',
  // The guest asked to be forgotten and the owner ran it (spec §7.6, 0061).
  anonymized: 'sms.howAnonymized',
} as const satisfies Record<NonNullable<ClientDetail['sms_opt_in_source']>, string>;

/**
 * The day she said yes, in the tenant's own calendar.
 *
 * Month and day, **plus the year when it is not this one**. A consent given in
 * 2019 rendered as "3 March" reads as three weeks ago, and the whole value of
 * this line is that somebody can tell a fresh yes from an ancient one at a
 * glance. No weekday: `formatDayLong` carries one because a booking is a day
 * somebody has to recognise, and a consent date is a fact somebody has to date.
 */
export function formatConsentDay(
  iso: string,
  timezone: string,
  nowMs: number,
  locale: UiLocale,
): string {
  const thisYear = isoDayIn(timezone, nowMs).slice(0, 4);
  const sameYear = isoDayIn(timezone, iso).slice(0, 4) === thisYear;
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    month: 'long',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(new Date(iso));
}
